import React, { FC, memo } from 'react'

import { useFragment } from '@apollo/client'
import { Celebration as BaseCelebration } from '@factorialco/f0-react/dist/experimental'
import { AddReactionDocument, CelebrationFragmentDoc } from 'generated/resources/graphql'
import capitalize from 'lodash/capitalize'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { COMMUNITY_ITEM_TYPES, COMMUNITY_WIDGET_TYPES } from 'modules/dashboard/lib/events'
import { emojiToTextCode, textCodeToEmoji } from 'modules/engagement/lib/reactions'

type Props = {
  id: string
  index: number
}

const Celebration: FC<Props> = ({ id, index }) => {
  const { access } = useCurrentSession()
  const [addReaction] = useMutation(AddReactionDocument, {
    extractorFn: (data) => data.postsMutations.createPostsReaction,
  })

  const { data } = useFragment({
    fragment: CelebrationFragmentDoc,
    fragmentName: 'Celebration',
    from: {
      __typename: 'CalendarCalendarEvent',
      id,
    },
    variables: {
      accessId: access.id,
    },
  })

  const employee = data.employee
  const employeeId = employee?.id

  const { navigation } = useNavigation()

  const eventType = data.eventType

  const firstName = employee?.preferredName || employee?.firstName
  const lastName = employee?.lastName

  const date = data.startsAt
    ? stringToDate(data.startsAt, 'backend_datetime_without_timezone').toDate()
    : undefined
  const post = data.post

  const avatar = buildUrl(employee?.access?.avatar)

  const typeF = (() => {
    if (eventType === 'birthday') {
      return 'birthday' as const
    }

    if (eventType === 'workiversary') {
      return 'anniversary' as const
    }

    if (eventType === 'firstDay') {
      return 'first-day' as const
    }
  })()

  const typeLabel = (() => {
    if (typeF === 'birthday') {
      return capitalize(i18n.tp('calendar.birthday', { count: 1 }))
    }

    if (typeF === 'anniversary') {
      return capitalize(i18n.tp('calendar.workiversary', { count: 1 }))
    }

    if (typeF === 'first-day') {
      return capitalize(i18n.tp('calendar.firstDay', { count: 1 }))
    }
  })()

  const eventItemType = (() => {
    if (typeF === 'birthday') {
      return COMMUNITY_ITEM_TYPES.BIRTHDAY
    }

    if (typeF === 'anniversary') {
      return COMMUNITY_ITEM_TYPES.WORKIVERSARY
    }

    if (typeF === 'first-day') {
      return COMMUNITY_ITEM_TYPES.NEW_JOINER
    }
  })()

  if (!firstName || lastName === undefined || !date || !typeLabel || !employeeId || !typeF) {
    return null
  }

  const employeeLink = navigation.employees.id.fullPath({
    id: employeeId,
  })

  const link = post?.id
    ? navigation.dashboard.post.id.fullPath({
        id: post.id,
      })
    : employeeLink

  const canReact = !!post?.id && !!post?.allowCommentsAndReactions
  const reactions = post?.reactions?.nodes || []
  const lastReactionCode = reactions[0]?.textCode
  const lastEmojiReaction = lastReactionCode ? textCodeToEmoji(lastReactionCode) : undefined

  const onReactionSelect = (emoji: string) => {
    const textCode = emojiToTextCode(emoji)
    if (post?.id && textCode) {
      addReaction({
        variables: {
          postId: post.id,
          textCode: textCode,
        },
      })
    }
  }

  const handleClick = () => {
    if (!eventItemType) return

    track(trackingEvents.dashboard.clickOnCommunityItem, {
      widget: COMMUNITY_WIDGET_TYPES.CELEBRATIONS,
      type: eventItemType,
      order: index + 1,
    })
  }

  return (
    <BaseCelebration
      firstName={firstName}
      lastName={lastName}
      date={date}
      src={avatar}
      onClick={handleClick}
      link={link}
      type={typeF}
      typeLabel={typeLabel}
      canReact={canReact}
      lastEmojiReaction={lastEmojiReaction}
      onReactionSelect={onReactionSelect}
    />
  )
}

export default memo(Celebration)
